import { useWsmPlugin } from "@/hooks/useWsmPlugin";
import { t } from "@lingui/macro";
import {
  useConfigContext,
  useGuardedUpdater,
  useSubscriptionContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWR from "swr";
import { RevenueCatEntitlement } from "@WahooFitness/cloud-client-types";

// Purchase-time SKU identifiers, distinct from the billing plan_code strings in ./planCodes.ts.
const packageIdentifiers = {
  pro: "$rc_monthly",
  core: "wahoo_core_monthly",
};

// Backoff between subscription refetches while waiting for a deferred downgrade's pending IAP
// change to land via the RevenueCat webhook. ~10s worst case; the poll early-exits on success.
const PENDING_IAP_POLL_DELAYS_MS = [1000, 2000, 3000, 4000];

const useRevenueCat = () => {
  const { sensorManagerPlugin } = useWsmPlugin();
  const { update, updateInProgress } = useGuardedUpdater();
  const { wahooToken } = useConfigContext();
  const { refetchSubscription } = useSubscriptionContext();

  const {
    data: offerings,
    error: offeringsError,
    isLoading: offeringsLoading,
  } = useSWRImmutable(["revCatOfferings"], async () => {
    const result = await sensorManagerPlugin.getRevenueCatOfferings();
    if (result.error) {
      throw new Error(result.error);
    } else {
      return result.packages;
    }
  });

  const {
    data: customerInfo,
    error: customerInfoError,
    isLoading: customerInfoLoading,
    mutate: mutateCustomerInfo,
  } = useSWR(["revCatCustomerInfo", wahooToken], async () => {
    const result = await sensorManagerPlugin.getRevenueCatCustomerInfo();
    if (result.error) {
      throw new Error(result.error);
    } else {
      return result.info;
    }
  });

  // True once the cloud reflects the (webhook-populated) pending IAP change.
  const pendingIapChangeLanded = useCallback(
    async () =>
      !!(await refetchSubscription().catch(() => undefined))?.subscription_pending_iap_change,
    [refetchSubscription]
  );

  // A deferred downgrade's pending change is written to the cloud only by an async RevenueCat
  // webhook, after the purchase returns, and native won't refetch on its own — so poll (check now,
  // then back off) until it lands or the budget is spent.
  const awaitPendingIapChange = useCallback(async () => {
    if (await pendingIapChangeLanded()) return;
    for (const delay of PENDING_IAP_POLL_DELAYS_MS) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      if (await pendingIapChangeLanded()) return;
    }
  }, [pendingIapChangeLanded]);

  // Runs after a successful purchase, in the BACKGROUND — deliberately not awaited by makePurchase:
  // awaiting the subscription refetch would flip the just-purchased state before the caller
  // navigates (an inline paywall swaps views mid-await and the following navigateBack lands on top,
  // reading as a double navigation). Upgrades/new purchases take effect immediately (one refetch);
  // a deferred downgrade polls for its pending change. Refresh rejections are swallowed.
  const refreshAfterPurchase = useCallback(
    async (pollForPendingChange: boolean) => {
      await mutateCustomerInfo().catch(() => undefined);
      if (pollForPendingChange) {
        await awaitPendingIapChange();
      } else {
        await refetchSubscription().catch(() => undefined);
      }
    },
    [mutateCustomerInfo, refetchSubscription, awaitPendingIapChange]
  );

  // Resolves with the native purchase result ({ completed, error }), or null if it threw.
  const makePurchase = useCallback(
    (packageIdentifier: string, opts?: { awaitPendingIapChange?: boolean }) =>
      update(() => sensorManagerPlugin.makeRevenueCatPurchase({ packageIdentifier }), {
        afterUpdate: () => void refreshAfterPurchase(!!opts?.awaitPendingIapChange),
      }),
    [sensorManagerPlugin, update, refreshAfterPurchase]
  );

  const getPeriodStringFromUnit = useCallback((unit?: number): string => {
    switch (unit) {
      case 0:
        return t`day`;
      case 1:
        return t`week`;
      case 2:
        return t`month`;
      case 3:
        return t`year`;
      default:
        return t`billing period`;
    }
  }, []);

  const convertPeriodToDays = useCallback((period?: { value: number; unit: number }): number => {
    const { value, unit } = period || {};
    if (unit === undefined || value === undefined) {
      return 0;
    }
    let multiplier = 1;
    switch (unit) {
      case 1: // week
        multiplier = 7;
        break;
      case 2: // month
        multiplier = 30;
        break;
      case 3: // year
        multiplier = 365;
        break;
    }
    return value * multiplier;
  }, []);

  const hasActiveCoreEntitlement =
    customerInfo?.entitlements?.all[RevenueCatEntitlement.CORE]?.isActive;
  const hasActiveProEntitlement =
    customerInfo?.entitlements?.all[RevenueCatEntitlement.PRO]?.isActive;

  const corePkg = offerings?.find((pkg) => pkg.identifier === packageIdentifiers.core);

  const showPurchaseOptions =
    !customerInfoLoading && !!customerInfo && !hasActiveCoreEntitlement && !hasActiveProEntitlement;
  const showUpgradeToPro =
    !customerInfoLoading && !!customerInfo && hasActiveCoreEntitlement && !hasActiveProEntitlement;
  const showDowngradeToCore = !customerInfoLoading && !!customerInfo && hasActiveProEntitlement;

  return {
    pkg: offerings?.[0],
    proPkg: offerings?.find((pkg) => pkg.identifier === packageIdentifiers.pro),
    corePkg,
    customerInfo,
    customerInfoError,
    customerInfoLoading,
    showPurchaseOptions,
    showUpgradeToPro,
    showDowngradeToCore,
    offeringsLoading,
    offeringsError,
    makePurchase,
    purchaseInProgress: updateInProgress,
    getPeriodStringFromUnit,
    convertPeriodToDays,
  };
};
export default useRevenueCat;
